// ─── components/Header/PhoneTabBar.jsx ────────────────────────────────────
// Bottom navigation on phones (the `phone:` variant: touch screens under
// 1024px): a floating ink dock. Picked over a white bar on 2026-10-20.
import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { PiHouseLight, PiMagnifyingGlassLight, PiBriefcaseLight, PiChatCircleLight, PiGraduationCapLight } from 'react-icons/pi';

// Pages that pin their own bar or composer to the bottom, or are a
// full-screen task, go without the tabs.
const HIDE_ON = [/^\/work\//, /\/learn$/, /^\/hire-talent\/listing\//, /^\/my-listings\/listing\//,
  /^\/job-postings\/[^/]+/, /^\/freelancer\//,
  /^\/seller-onboarding/, /^\/signup$/, /^\/login$/, /^\/find-work\/(create|edit)-listing/, /^\/academy\/tutorials\/[^/]+\/edit$/, /^\/academy\/courses\/[^/]+$/, /^\/academy\/seminars\/[^/]+$/, /^\/get-started/, /^\/client-onboarding/, /^\/academy\/create/, /^\/instructor\/(grade|module-submission)\//];

function Count({ value, tone }) {
  if (!value) return null;
  return (
    <span className={`absolute -top-1 left-1/2 ml-1.5 grid h-5 min-w-5 place-items-center rounded-full px-1 text-[12px] font-extrabold tabular-nums ${tone}`}>
      {value > 9 ? '9+' : value}
    </span>
  );
}
Count.propTypes = { value: PropTypes.number, tone: PropTypes.string };

export default function PhoneTabBar({ isHiring, workCount, inboxCount, active }) {
  const { pathname } = useLocation();
  const hide = HIDE_ON.some(re => re.test(pathname));
  // Room under the page so the bar never covers the end of it (index.css).
  useEffect(() => {
    document.documentElement.classList.toggle('has-tabbar', !hide);
    return () => document.documentElement.classList.remove('has-tabbar');
  }, [hide]);
  if (hide) return null;

  const exploreTo = isHiring ? '/hire-talent' : '/job-postings';
  const tabs = [
    { key: 'home', to: '/', label: 'Home', Icon: PiHouseLight },
    { key: 'explore', to: exploreTo, label: 'Explore', Icon: PiMagnifyingGlassLight },
    // The dashboard home (its hub), not straight into the Work list.
    { key: 'work', to: isHiring ? '/hiring/dashboard' : '/seller/dashboard', label: 'Work', Icon: PiBriefcaseLight, count: workCount },
    { key: 'inbox', to: '/inbox', label: 'Inbox', Icon: PiChatCircleLight, count: inboxCount },
    { key: 'academy', to: '/academy', label: 'Academy', Icon: PiGraduationCapLight },
  ];
  return (
    <nav aria-label="Main" className="fixed inset-x-3 bottom-[max(12px,env(safe-area-inset-bottom))] z-30 hidden font-sans phone:block">
      <ul className="m-0 grid list-none grid-cols-5 gap-1 rounded-full bg-press-ink p-1.5 shadow-[0_0_0_1px_rgba(255,255,255,0.06),0_18px_40px_-14px_rgba(18,22,28,0.6)]">
        {tabs.map(t => (
          <li key={t.key}>
            <Link to={t.to} aria-current={active === t.key ? 'page' : undefined}
              className={`flex h-12 flex-col items-center justify-center gap-0.5 rounded-full text-[12px] font-semibold no-underline transition-colors duration-300 ease-spring ${active === t.key ? 'bg-white text-press-ink' : 'text-white/70'}`}>
              <span className="relative"><t.Icon size={21} aria-hidden="true" /><Count value={t.count} tone="bg-press-brass text-press-ink" /></span>
              {t.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
PhoneTabBar.propTypes = {
  isHiring: PropTypes.bool,
  workCount: PropTypes.number,
  inboxCount: PropTypes.number,
  active: PropTypes.string,
};
